import { useMemo, type FC } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useModalConfirm } from "@ryde/redux";
import {
  capitalize,
  ConfirmResult,
  formatHHmmTime,
  TimeZoneEnum,
  useFormats,
  type DirectionEnum,
  type ICourseStationInfo,
} from "@ryde/shared";

import { AvailabilityDirectionLabel } from "../../../common/course-availability";
import { ColorIconButton } from "../../../controls";
import { CsvCellTemplate } from "../../templates-table";
import { RouteScheduleItemDisplayStyled } from "./route-schedule-item.display.styled";
import { type IRouteScheduleForm } from "./table-route-schedule.models";

export const RouteScheduleItemDisplay: FC<IRouteScheduleForm> = ({
  disabledEdit,
  disabledRemove,
  removeHandler,
  index,
  onEdit,
  value,
}) => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const { timeFormat } = useFormats();
  const courseDirectionTemplate = useWatch({ name: "metadata.direction" }) as
    DirectionEnum | undefined;
  const stationsInfo = useWatch({ name: "metadata.stationsInfo" }) as
    ICourseStationInfo[] | undefined;

  const directions = useMemo(
    () => [
      { value: "A", label: t("selectListLabel.DirA") },
      { value: "B", label: t("selectListLabel.DirB") },
    ],
    [t],
  );

  const directionLabel = useMemo(
    () =>
      directions.find(item => item.value === value?.direction)?.label ??
      value?.direction ??
      "",
    [directions, value?.direction],
  );

  const onArchiveClickEvent = useEvent(async (): Promise<void> => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t("general.ITEM_ARCHIVE_MESSAGE"),
    });

    if (confirmationResult === ConfirmResult.OK) {
      removeHandler?.(index || 0);
    }
  });

  return (
    <RouteScheduleItemDisplayStyled className="display-mode">
      <div className="route-schedule-item-display__content">
        {!!value?.dayTypeIds?.length && (
          <div className="route-schedule-item-display__row">
            <label>{capitalize(t("dayType.plural"))}:</label>
            <div>
              <CsvCellTemplate
                name="dayTypeIds"
                value={value.dayTypeIds}
                formAttributes={{ dropItemsSource: "dayType" }}
              />
            </div>
          </div>
        )}
        {!!value?.dayGroupIds?.length && (
          <div className="route-schedule-item-display__row">
            <label>{capitalize(t("dayGroup.plural"))}:</label>
            <div>
              <CsvCellTemplate
                name="dayGroupIds"
                value={value.dayGroupIds}
                formAttributes={{ dropItemsSource: "dayGroup" }}
              />
            </div>
          </div>
        )}
        {!!value?.time && (
          <div className="route-schedule-item-display__row">
            <label>{t("general.TIME")}:</label>
            <div>
              <p>{formatHHmmTime(value.time, timeFormat, TimeZoneEnum.UTC)}</p>
            </div>
          </div>
        )}
        {!!value?.direction && (
          <div className="route-schedule-item-display__row">
            <label>{t("general.DIRECTION")}:</label>
            <div>
              <AvailabilityDirectionLabel
                scheduleDirection={value.direction}
                courseDirectionTemplate={courseDirectionTemplate}
                stationsInfo={stationsInfo}
                label={directionLabel}
              />
            </div>
          </div>
        )}
      </div>
      <div className="route-schedule-item-display__actions">
        {!disabledEdit && (
          <ColorIconButton
            iconType="EDIT"
            edge="end"
            aria-label="edit"
            onClick={() => onEdit?.()}
          />
        )}
        {!disabledRemove && (
          <ColorIconButton
            iconType="DELETE"
            edge="end"
            aria-label="delete"
            onClick={onArchiveClickEvent}
          />
        )}
      </div>
    </RouteScheduleItemDisplayStyled>
  );
};
